import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { ChatMessage } from '../../contexts/ChatContext';
import FileUploadButton from './FileUploadButton';
import MessageStatus from './MessageStatus';
import TypingIndicator from './TypingIndicator';
import OnlineStatus from './OnlineStatus';
import { useAuth } from '../../contexts/AuthContext';

interface ChatInterfaceProps {
  errandId: string;
  errandTitle: string;
  onClose?: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ errandId, errandTitle, onClose }) => {
  const { messages, sendMessage, fetchMessages, markMessagesAsRead, loading, error } = useChat();
  const { user } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Fetch messages when component mounts
  useEffect(() => {
    fetchMessages(errandId);
    markMessagesAsRead(errandId);
  }, [errandId, fetchMessages, markMessagesAsRead]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle sending message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!newMessage.trim() || loading) return;

    const messageText = newMessage.trim();
    setNewMessage(''); // Clear input immediately for better UX

    try {
      const success = await sendMessage({
        errandId,
        message: messageText,
        messageType: 'text'
      });

      if (success) {
        // Refetch messages to ensure UI is updated
        await fetchMessages(errandId);
        // Trigger chat rooms refresh
        window.dispatchEvent(new CustomEvent('messageSent', { detail: { errandId } }));
        // Focus input after a short delay to ensure rendering is complete
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      } else {
        // Restore message if send failed
        setNewMessage(messageText);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Restore message on error
      setNewMessage(messageText);
    }
  };

  // Handle typing indicator
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    setIsTyping(e.target.value.length > 0);
  };

  // Format message time
  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      if (diffInMinutes < 1) return 'Just now';
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Check if message is from current user
  const isOwnMessage = (message: ChatMessage) => {
    if (!user) return false;
    // Get user ID from token or profile
    const token = localStorage.getItem('token');
    if (token && message.sender_id) {
      // In a real implementation, decode JWT to get user ID
      // For now, we'll use a simple check based on sender_name
      return message.sender_name === user.name || (user as any).full_name === message.sender_name;
    }
    return false;
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl shadow-xl overflow-hidden" style={{ minHeight: '400px', maxHeight: '100%' }}>
      <style>{`
        .chat-container {
          overflow-x: hidden;
          width: 100%;
        }
        .chat-container * {
          max-width: 100%;
          box-sizing: border-box;
        }
      `}</style>
      {/* Modern Chat Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50 flex-shrink-0 overflow-x-hidden">
        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
          <div className="relative flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-md">
              <span className="text-white text-sm sm:text-lg font-bold">{errandTitle.charAt(0).toUpperCase()}</span>
            </div>
            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 border-2 border-white rounded-full"></div>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{errandTitle}</h3>
            <p className="text-xs text-gray-500">Errand Chat</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Search Bar */}
      {messages.length > 0 && (
        <div className="p-2 sm:p-3 border-b border-gray-200 bg-white flex-shrink-0">
          <div className="relative">
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 pl-8 sm:pl-10 pr-8 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-2 sm:pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Messages Container with modern styling */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 space-y-4 bg-gradient-to-b from-white via-gray-50 to-white"
        style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(34, 197, 94, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)'
        }}
      >
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-3 text-red-800 text-sm animate-pulse">
            <div className="flex items-center">
              <span className="mr-2">⚠️</span>
              {error}
            </div>
          </div>
        )}

        {loading && messages.length === 0 ? (
          <div className="flex justify-center items-center h-32">
            <div className="flex flex-col items-center space-y-3">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-green-200 border-t-green-600"></div>
              <p className="text-sm text-gray-500">Loading messages...</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            <div className="w-20 h-20 mx-auto mb-4 text-gray-300">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="opacity-50">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="font-medium text-gray-700 mb-1">No messages yet</p>
            <p className="text-sm text-gray-500">Start the conversation!</p>
          </div>
        ) : (
          messages
            .filter((message) => {
              if (!searchQuery) return true;
              const query = searchQuery.toLowerCase();
              return message.message.toLowerCase().includes(query) ||
                     message.sender_name?.toLowerCase().includes(query);
            })
            .map((message, index) => {
            const ownMessage = isOwnMessage(message);
            const showAvatar = index === 0 || messages[index - 1].sender_id !== message.sender_id;
            const showTime = index === messages.length - 1 ||
              new Date(messages[index + 1].created_at).getTime() - new Date(message.created_at).getTime() > 300000; // 5 minutes

            return (
              <div
                key={message.id}
                className={`flex items-end space-x-2 ${ownMessage ? 'flex-row-reverse space-x-reverse' : ''} animate-fadeIn`}
              >
                {/* Avatar */}
                {showAvatar && !ownMessage && (
                  <div className="w-8 h-8 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                    <span className="text-white text-xs font-semibold">
                      {message.sender_name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                )}
                {showAvatar && ownMessage && <div className="w-8"></div>}

                {/* Message Bubble */}
                <div className={`flex flex-col ${ownMessage ? 'items-end' : 'items-start'} max-w-[85%] sm:max-w-[75%] md:max-w-md`}>
                  {!ownMessage && showAvatar && (
                    <span className="text-xs text-gray-500 mb-1 px-2">{message.sender_name}</span>
                  )}
                  <div
                    className={`px-4 py-2.5 rounded-2xl shadow-sm transition-all duration-200 ${
                      ownMessage
                        ? 'bg-gradient-to-br from-green-500 to-green-600 text-white rounded-br-md'
                        : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'
                    }`}
                  >
                    <p className="break-words text-sm leading-relaxed">{message.message}</p>

                    {/* File attachment */}
                    {message.file_url && (
                      <div className={`mt-2 p-2 rounded-lg ${ownMessage ? 'bg-white bg-opacity-20' : 'bg-gray-50'}`}>
                        <a
                          href={message.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 text-sm hover:underline"
                        >
                          <span>📎</span>
                          <span className="truncate">{message.file_name || 'File attachment'}</span>
                          {message.file_size && (
                            <span className={`text-xs ${ownMessage ? 'text-white opacity-75' : 'text-gray-500'}`}>
                              ({(Math.round((message.file_size / 1024) * 10) / 10).toFixed(1)} KB)
                            </span>
                          )}
                        </a>
                        {message.file_type?.startsWith('image/') && (
                          <img
                            src={message.file_url}
                            alt={message.file_name || 'Image'}
                            className="mt-2 max-w-full rounded-lg shadow-sm"
                            style={{ maxHeight: '200px' }}
                          />
                        )}
                      </div>
                    )}

                    {/* Message metadata for special types */}
                    {message.message_type === 'location' && message.metadata?.location && (
                      <div className={`mt-2 p-2 rounded-lg text-xs ${ownMessage ? 'bg-white bg-opacity-20' : 'bg-gray-50'}`}>
                        📍 Location shared
                      </div>
                    )}

                    {message.message_type === 'status_update' && message.metadata?.status && (
                      <div className={`mt-2 p-2 rounded-lg text-xs ${ownMessage ? 'bg-white bg-opacity-20' : 'bg-gray-50'}`}>
                        🔄 Status: {message.metadata.status}
                      </div>
                    )}
                  </div>
                  <MessageStatus
                    status="sent"
                    timestamp={message.created_at}
                    isOwnMessage={ownMessage}
                  />
                </div>
              </div>
            );
          })
        )}

        {/* Enhanced Typing Indicator */}
        <TypingIndicator isTyping={isTyping} />

        <div ref={messagesEndRef} />
      </div>

      {/* Modern Message Input */}
      <div className="p-2 sm:p-3 md:p-4 border-t border-gray-200 bg-white flex-shrink-0 overflow-x-hidden">
        <form onSubmit={handleSendMessage} className="flex items-end space-x-2">
          <FileUploadButton
            errandId={errandId}
            onFileUploaded={async (fileUrl, fileName, fileType, fileSize) => {
              await sendMessage({
                errandId,
                message: `📎 ${fileName}`,
                messageType: 'file',
                fileUrl,
                fileName,
                fileType,
                fileSize
              });
              await fetchMessages(errandId);
              window.dispatchEvent(new CustomEvent('messageSent', { detail: { errandId } }));
            }}
            disabled={loading}
          />
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={handleInputChange}
              placeholder="Type your message..."
              className="w-full px-4 py-2.5 pr-12 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              disabled={loading}
            />
            {newMessage.trim() && (
              <button
                type="submit"
                disabled={loading}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-green-500 text-white rounded-full hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            )}
          </div>
          {!newMessage.trim() && (
            <button
              type="submit"
              disabled={!newMessage.trim() || loading}
              className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg transform hover:scale-105"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default ChatInterface;
